// ─── La misura delle FASCE: dentro una casella c'è il PASSO ─────────────────
//
// 🗣️ Sua, 18/09/2026: *«ogni riquadro è da 30 minuti e poi le partite possono
// essere da 60 o da 90 minuti»* · *«o anche oltre i 90, per esempio se sono dei
// corsi o dei tornei»* · *«io posso lasciare un periodo di tempo con solamente
// microcampi prenotabili ogni 30 minuti… mentre magari in altri posti decido di
// fissare già degli slot fissi o da 60 o da 90 minuti»*.
//
// 🚨⭐ **IL NUMERO NELLA CASELLA NON È LA DURATA DELLA PARTITA.** È ogni quanto
// si può COMINCIARE. In una banda da 30 ci sta una partita da 60, una da 90 e
// un corso da 120 — basta che finisca dentro la banda. Scriverlo qui perché è
// l'unico equivoco che questa schermata può generare, e genererebbe prezzi e
// prenotazioni sbagliate senza dare nessun errore.
//
// 🔄⭐⭐ **E DAL 18/09/2026 UNO SLOT È UN RIQUADRO SOLO, non tre caselle uguali
// in fila.** 🗣️ Lui: *«ci ho messo un po' a capirlo, che quella barretta verde
// sulla sinistra sia l'inizio dello slot da 90 minuti. Ci potrebbe essere un
// altro metodo grafico?»* ⇒ Messe cinque proposte a confronto, ha scelto:
// *«un riquadro alto per slot, e dentro c'è scritto da che ora a che ora»*.
// ⚖️ **Perché era difficile da leggere, e non era colpa sua**: il foglio di
// stile dà a ogni `<td>` il suo bordo arrotondato e 1 px di distacco ⇒ **il
// riquadro ERA la mezz'ora**. Tre riquadri identici con dentro lo stesso numero
// tre volte si leggono come **tre cose**, e la barretta verde era una riga sola
// a dire il contrario — una contro tre.
// 📌 *In una griglia oraria l'altezza è già una lingua che tutti leggono: alto
//    vuol dire lungo. Il foglio si rifiutava di usarla.*

import type { PostoNelloSlot } from '@pmo/shared';
import { PASSO_CASELLA } from '@pmo/shared';
import type { Misura } from '../foglio/misura.js';

/**
 * Lo stato di una casella, che il foglio **non interpreta**: lo passa e basta.
 *
 * Forma: `"<parole> | <etichetta>"` — le parole diventano classi, l'etichetta è
 * l'intervallo dello slot (`09:30–11:00`) e la legge solo chi la mostra.
 *
 * ⚠️ `SPENTA` da sola resta una parola valida, e non è per pigrizia: è quello
 * che il foglio **rimanda indietro** quando si spegne una selezione, dove un
 * posto e un intervallo non esistono ancora.
 */
export const SPENTA = 'spenta';
/**
 * ⭐ La casella **non è di questo campo**: la sta prestando «tutti i campi».
 *
 * 🗣️ Nasce il 18/09/2026 da *«sì, un campo può avere orari di giorni in comune
 * e orari di giorni suoi»*. ⇒ Guardando il foglio di un campo bisogna poter
 * distinguere a colpo d'occhio cosa è **suo** da cosa **eredita**, o si crede di
 * aver deciso una cosa che in realtà si sta solo guardando.
 * ⚖️ È una parola e non un colore perché il colore qui è già preso: dice il
 * passo (⇒ `SCALA`). Le classi si sommano, le tinte no.
 */
export const IN_COMUNE = 'in-comune';
const TAGLIO = '|';

export function segno(p: {
  spenta: boolean;
  posto?: PostoNelloSlot;
  /** L'intervallo dello slot, su TUTTE le sue caselle: serve anche ai titoli. */
  etichetta?: string | null;
  /** ⭐ Viene da «tutti i campi», non dal campo che si sta guardando. */
  inComune?: boolean;
}): string | undefined {
  const parole = [p.spenta ? SPENTA : '', p.posto ?? '', p.inComune === true ? IN_COMUNE : '']
    .filter(Boolean)
    .join(' ');
  if (parole === '' && !p.etichetta) return undefined;
  return p.etichetta ? `${parole}${TAGLIO}${p.etichetta}` : parole;
}

const parole = (s?: string): string => (s ?? '').split(TAGLIO)[0] ?? '';
export const eSpenta = (s?: string): boolean => parole(s).includes(SPENTA);
export const eInComune = (s?: string): boolean => parole(s).includes(IN_COMUNE);
const posto = (s?: string): PostoNelloSlot | undefined =>
  (['apre', 'dentro', 'chiude', 'resto'] as const).find((x) => parole(s).includes(x));
const etichetta = (s?: string): string | undefined => (s ?? '').split(TAGLIO)[1];

/** I passi che il server accetta (`creaFasciaSchema`: da 5 a 240 minuti). */
function passoBuono(n: number): boolean {
  return Number.isInteger(n) && n >= 5 && n <= 240;
}

/** «90» → 90 · «90 min» → 90. `null` su ciò che non è un passo. */
export function minutiDelPasso(testo: string): number | null {
  const pulito = testo
    .trim()
    .replace(/\s/g, '')
    .replace(/min(uti)?$/i, '')
    .replace(/'$/, '');
  if (!/^\d{1,3}$/.test(pulito)) return null;
  const n = Number(pulito);
  return passoBuono(n) ? n : null;
}

/**
 * La scala del colore va col passo: più raro è l'inizio, più scura è la casella.
 *
 * ⚖️ Non è decorazione — è la ragione per cui una griglia è più sicura di un
 * elenco: una casella col passo sbagliato in mezzo a una banda **ha un'altra
 * tinta**, e si vede senza leggere. In un elenco di righe non si vede affatto.
 */
const SCALA: Record<number, number> = { 15: 12, 30: 20, 45: 28, 60: 36, 90: 48, 120: 58 };

export const passo: Misura = {
  leggi: minutiDelPasso,
  /**
   * ⭐ **L'intervallo si scrive UNA VOLTA, in cima al blocco.** Le caselle che
   * continuano lo slot restano mute: è il blocco a parlare, non le sue righe.
   *
   * ⚠️ E si scrive solo quando il blocco è più alto di una riga. Col passo da
   * 30 ogni slot è **una casella**, e «12:30–13:00» ripetuto su tutta la
   * giornata direbbe meno di «30» occupando il triplo.
   */
  breve: (v, stato) => {
    const p = posto(stato);
    if (p === 'resto') return 'avanza';
    if (p === 'dentro' || p === 'chiude') return '';
    const e = etichetta(stato);
    const testo = p === 'apre' && v > PASSO_CASELLA && e !== undefined ? e : String(v);
    return eSpenta(stato) ? `(${testo})` : testo;
  },
  /** Nella casella si batte il PASSO, sempre: l'intervallo è solo da leggere. */
  battuto: (v) => String(v),
  /*
   * ⚠️ Una casella SPENTA non prende il colore della sua banda: prenderebbe
   * l'occhio esattamente come una accesa, e le due dicono cose opposte a chi
   * prenota. Il suo aspetto è il tratteggio, che sta nel foglio di stile.
   * ⛔ E nemmeno il RESTO lo prende: lì non si comincia niente, quindi tingerlo
   * come la banda direbbe il falso proprio dove la banda finisce di valere.
   */
  tinta: (v, _tutte, stato) => {
    if (eSpenta(stato) || posto(stato) === 'resto') return undefined;
    return `color-mix(in srgb, var(--verde) ${SCALA[v] ?? 40}%, transparent)`;
  },
  /**
   * ⭐ `sola` = lo slot sta in UNA casella (il passo da 30), quindi il blocco
   * non ha né mezzo né fondo e resta un riquadro chiuso da tutte le parti.
   * Senza, due slot da mezz'ora attaccati si salderebbero fra loro e la
   * colonna sembrerebbe una banda unica.
   */
  classe: (v, stato) =>
    [
      eSpenta(stato) ? SPENTA : '',
      posto(stato) ?? '',
      v <= PASSO_CASELLA ? 'sola' : '',
      eInComune(stato) ? IN_COMUNE : '',
    ]
      .filter(Boolean)
      .join(' '),
  /**
   * 🚨 Il titolo porta **tutto**, ed è più importante di prima, non meno: nel
   * blocco saldato le caselle di mezzo non hanno più testo, e a chi legge con
   * la tastiera o col lettore di schermo il riquadro alto non dice niente.
   */
  dice(v, giorno, ora, stato) {
    if (v === undefined) return `${giorno} ${ora}: i soci non prenotano`;
    const e = etichetta(stato);
    const p = posto(stato);
    const dove =
      p === 'resto'
        ? `qui non ci sta un altro slot da ${v} minuti: la fascia arriva fin qui, ma non abbastanza`
        : e === undefined
          ? `si comincia ogni ${v} minuti`
          : p === 'apre'
            ? `qui comincia uno slot da ${v} minuti: ${e}`
            : `dentro lo slot ${e}, da ${v} minuti`;
    const frase = eSpenta(stato) ? `${giorno} ${ora}: fascia SPENTA — ${dove}` : `${giorno} ${ora}: ${dove}`;
    /*
     * 🚨 Chi guarda il foglio di un campo deve sapere che questa casella non è
     * sua PRIMA di scriverci: scrivendoci, il giorno si stacca da «tutti i
     * campi». Il titolo è l'unico posto che lo può dire anche a chi il foglio
     * lo ascolta invece di guardarlo, e a chi non distingue il grigio.
     */
    return eInComune(stato) ? `${frase} — viene da «tutti i campi»` : frase;
  },
  niente: '–',
  segnaposto: 'min',
  etichettaBarra: 'Ogni quanti minuti si comincia, nelle caselle scelte',
  etichettaGriglia: 'Le caselle delle fasce prenotabili',
  modo: 'numeric',
  comeSiChiama: 'Passo',
  saldaLeCorse: true,
};
